import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Edit, Save, X, Eye, EyeOff, Briefcase, MapPin, Clock, DollarSign } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

const JOB_TYPES = [
  { value: "full-time", label: "Full-Time" },
  { value: "part-time", label: "Part-Time" },
  { value: "contract", label: "Contract" },
  { value: "internship", label: "Internship" },
];

const DEPARTMENTS = [
  "Broadcasting",
  "Production",
  "Engineering",
  "Marketing",
  "Sales",
  "Operations",
  "Content",
  "Design",
  "Administration",
  "Other"
];

const emptyJob = {
  title: "",
  department: "",
  location: "",
  type: "full-time",
  description: "",
  requirements: "",
  benefits: "",
  salary_range: "",
  is_active: true
};

export const CareersTab = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyJob);
  const [saving, setSaving] = useState(false);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/careers?active_only=false`, { headers: getAuthHeader() });
      setJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchJobs(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/careers`, formData, { headers: getAuthHeader() });
      await fetchJobs();
      setShowForm(false);
      setFormData(emptyJob);
    } catch (error) {
      console.error("Error creating job:", error);
    }
    setSaving(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/careers/${editingId}`, formData, { headers: getAuthHeader() });
      await fetchJobs();
      setEditingId(null);
      setFormData(emptyJob);
    } catch (error) {
      console.error("Error updating job:", error);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this job posting?")) return;
    try {
      await axios.delete(`${API}/careers/${id}`, { headers: getAuthHeader() });
      setJobs(jobs.filter(j => j.id !== id));
    } catch (error) {
      console.error("Error deleting job:", error);
    }
  };

  const handleToggleStatus = async (job) => {
    try {
      await axios.patch(`${API}/careers/${job.id}/toggle`, {}, { headers: getAuthHeader() });
      await fetchJobs();
    } catch (error) {
      console.error("Error toggling status:", error);
    }
  };

  const startEdit = (job) => {
    setEditingId(job.id);
    setFormData({
      title: job.title,
      department: job.department,
      location: job.location,
      type: job.type,
      description: job.description,
      requirements: job.requirements || "",
      benefits: job.benefits || "",
      salary_range: job.salary_range || "",
      is_active: job.is_active
    });
    setShowForm(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData(emptyJob);
  };

  const JobForm = ({ onSubmit, isEdit = false }) => (
    <form onSubmit={onSubmit} className="p-6 bg-zinc-900/50 border border-white/10 space-y-4">
      <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4">
        {isEdit ? "Edit Job Posting" : "Create New Job Posting"}
      </h3>
      
      {/* Row 1: Title & Department */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Job Title *</label>
          <input 
            type="text" 
            value={formData.title} 
            onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
            placeholder="e.g. Camera Operator"
            required 
            className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-white/30" 
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Department *</label>
          <select 
            value={formData.department} 
            onChange={(e) => setFormData({ ...formData, department: e.target.value })} 
            required
            className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
          >
            <option value="">Select Department</option>
            {DEPARTMENTS.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 2: Location, Type, Salary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Location *</label>
          <input 
            type="text" 
            value={formData.location} 
            onChange={(e) => setFormData({ ...formData, location: e.target.value })} 
            placeholder="e.g. Remote, New York, NY"
            required 
            className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Job Type *</label>
          <select 
            value={formData.type} 
            onChange={(e) => setFormData({ ...formData, type: e.target.value })} 
            required
            className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm"
          >
            {JOB_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Salary Range</label>
          <input 
            type="text" 
            value={formData.salary_range} 
            onChange={(e) => setFormData({ ...formData, salary_range: e.target.value })} 
            placeholder="e.g. $50,000 - $70,000"
            className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Job Description *</label>
        <textarea 
          value={formData.description} 
          onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
          placeholder="Describe the role and responsibilities..."
          required
          rows={4}
          className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm resize-none" 
        />
      </div>

      {/* Requirements */}
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Requirements</label>
        <textarea 
          value={formData.requirements} 
          onChange={(e) => setFormData({ ...formData, requirements: e.target.value })} 
          placeholder="List qualifications and requirements (one per line)..."
          rows={3}
          className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm resize-none" 
        />
      </div>

      {/* Benefits */}
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Benefits</label>
        <textarea 
          value={formData.benefits} 
          onChange={(e) => setFormData({ ...formData, benefits: e.target.value })} 
          placeholder="List benefits offered..."
          rows={2}
          className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm resize-none" 
        />
      </div>

      {/* Active Toggle */}
      <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
        <input 
          type="checkbox" 
          checked={formData.is_active} 
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} 
          className="rounded"
        />
        <Eye className="w-4 h-4" />
        Publish immediately (make visible on careers page)
      </label>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2">
        <Button type="submit" disabled={saving} className="bg-white text-black hover:bg-zinc-200 text-xs px-4 py-2 h-auto rounded-none">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : (isEdit ? "Update Job" : "Create Job")}
        </Button>
        <Button 
          type="button" 
          onClick={() => { isEdit ? cancelEdit() : setShowForm(false); setFormData(emptyJob); }} 
          className="bg-zinc-800 text-white hover:bg-zinc-700 text-xs px-4 py-2 h-auto rounded-none"
        >
          Cancel
        </Button>
      </div>
    </form>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">Careers Manager</h2>
          <p className="text-zinc-500 text-sm mt-1">Create and manage job postings for your careers page</p>
        </div>
        <Button 
          onClick={() => { setShowForm(!showForm); setEditingId(null); setFormData(emptyJob); }} 
          className="bg-white text-black hover:bg-zinc-200 text-xs px-4 py-2 h-auto rounded-none"
        >
          <Plus className="w-4 h-4 mr-2" />Add Job Posting
        </Button>
      </div>

      {/* Add New Form */}
      {showForm && !editingId && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <JobForm onSubmit={handleCreate} />
        </motion.div>
      )}

      {/* Jobs List */}
      <div className="space-y-3">
        {jobs.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/30 border border-white/5">
            <Briefcase className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No job postings yet. Create your first job posting above.</p>
          </div>
        ) : (
          jobs.map((job, index) => (
            <motion.div 
              key={job.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: index * 0.05 }}
              className={`border ${job.is_active ? 'border-white/10 bg-zinc-900/50' : 'border-zinc-800 bg-zinc-900/20'}`}
            >
              {editingId === job.id ? (
                <JobForm onSubmit={handleUpdate} isEdit />
              ) : (
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className={`font-bold text-lg ${job.is_active ? 'text-white' : 'text-zinc-500'}`}>
                          {job.title}
                        </h3>
                        {!job.is_active && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-500 uppercase">Draft</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {job.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {JOB_TYPES.find(t => t.value === job.type)?.label || job.type}
                        </span>
                        {job.salary_range && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            {job.salary_range}
                          </span>
                        )}
                      </div>
                      <p className="text-zinc-500 text-sm mt-2 line-clamp-2">{job.description}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => handleToggleStatus(job)} 
                        className={`p-2 ${job.is_active ? 'text-green-500 hover:text-green-400' : 'text-zinc-600 hover:text-zinc-400'}`}
                        title={job.is_active ? "Unpublish" : "Publish"}
                      >
                        {job.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button 
                        onClick={() => startEdit(job)} 
                        className="p-2 text-zinc-500 hover:text-white"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(job.id)} 
                        className="p-2 text-zinc-500 hover:text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>

      {/* Stats */}
      {jobs.length > 0 && (
        <div className="flex items-center gap-6 text-sm text-zinc-500">
          <span>{jobs.filter(j => j.is_active).length} active postings</span>
          <span>{jobs.filter(j => !j.is_active).length} drafts</span>
        </div>
      )}
    </motion.div>
  );
};

export default CareersTab;
